import React from "react";
import * as R from "ramda";
import { Form, Modal, Checkbox, Select, Icon, Tooltip, Radio } from "antd";
import { withForm } from "@worcket/common/hocs";

import {
  getCandidateSelectionPermissionMode,
  getInviteUserFormPermissions,
  LIKE_PERMISSION,
  LIKE_SCREENING_RESTRICTED_PERMISSION,
  normalizeOpportunityUserPermissions,
  setCandidateSelectionPermissionEnabled,
  setCandidateSelectionPermissionMode
} from "./permissions";

const Option = Select.Option;

const styleCheckBox = {
  display: "block",
  margin: 0
};

const permissionOptionContentStyle = {
  alignItems: "center",
  columnGap: 8,
  display: "inline-flex",
  flexWrap: "wrap",
  lineHeight: 1.4,
  rowGap: 2
};

const permissionOptionHeaderStyle = {
  alignItems: "center",
  display: "inline-flex",
  gap: 8
};

const permissionSectionStyle = {
  marginBottom: 12
};

const permissionGroupLabelStyle = {
  color: "rgba(0, 0, 0, 0.85)",
  display: "block",
  fontSize: 12,
  fontWeight: 500,
  margin: "12px 0 8px 24px"
};

const nestedOptionsStyle = {
  marginLeft: 24
};

const radioOptionStyle = {
  display: "block",
  marginBottom: 12
};

const radioDescriptionStyle = {
  color: "rgba(0, 0, 0, 0.65)",
  fontSize: 12,
  lineHeight: 1.4,
  paddingLeft: 24,
  paddingTop: 4
};

const permissionInfoIconStyle = {
  color: "rgba(0, 0, 0, 0.45)"
};

const PermissionsField = ({ value = [], onChange, t }) => {
  const permissions = R.defaultTo([], value);
  const candidateSelectionMode = getCandidateSelectionPermissionMode(
    permissions
  );
  const hasCandidateSelectionPermission = Boolean(candidateSelectionMode);

  const emitChange = nextPermissions => {
    if (onChange) {
      onChange(nextPermissions);
    }
  };

  const handlePermissionToggle = permission => event => {
    const checked = event.target.checked;

    emitChange(
      checked
        ? R.uniq(permissions.concat(permission))
        : permissions.filter(
            currentPermission => currentPermission !== permission
          )
    );
  };

  const handleCandidateSelectionToggle = event => {
    emitChange(
      setCandidateSelectionPermissionEnabled(
        permissions,
        event.target.checked,
        LIKE_PERMISSION
      )
    );
  };

  const handleCandidateSelectionModeChange = event => {
    emitChange(
      setCandidateSelectionPermissionMode(permissions, event.target.value)
    );
  };

  return (
    <div>
      <div style={permissionSectionStyle}>
        <Checkbox
          checked={permissions.includes("chat")}
          onChange={handlePermissionToggle("chat")}
          style={styleCheckBox}
        >
          {t("opportunity:users.permissionsList.chat")}
        </Checkbox>
      </div>

      <div style={permissionSectionStyle}>
        <Checkbox
          checked={hasCandidateSelectionPermission}
          onChange={handleCandidateSelectionToggle}
          style={styleCheckBox}
        >
          <span style={permissionOptionContentStyle}>
            <span style={permissionOptionHeaderStyle}>
              <span>{t("opportunity:users.permissionsList.like")}</span>
              <Tooltip title={t("opportunity:users.permissionLegend.like")}>
                <Icon type="info-circle" style={permissionInfoIconStyle} />
              </Tooltip>
            </span>
          </span>
        </Checkbox>

        {hasCandidateSelectionPermission && (
          <div style={nestedOptionsStyle}>
            <span style={permissionGroupLabelStyle}>
              {t("opportunity:users.candidateSelectionAdvanceMode")}
            </span>
            <Radio.Group
              onChange={handleCandidateSelectionModeChange}
              value={candidateSelectionMode}
            >
              <div style={radioOptionStyle}>
                <Radio value={LIKE_PERMISSION}>
                  {t(
                    `opportunity:users.candidateSelectionMode.${LIKE_PERMISSION}`
                  )}
                </Radio>
                <div style={radioDescriptionStyle}>
                  {t(
                    `opportunity:users.candidateSelectionModeDescription.${LIKE_PERMISSION}`
                  )}
                </div>
              </div>

              <div style={radioOptionStyle}>
                <Radio value={LIKE_SCREENING_RESTRICTED_PERMISSION}>
                  {t(
                    `opportunity:users.candidateSelectionMode.${LIKE_SCREENING_RESTRICTED_PERMISSION}`
                  )}
                </Radio>
                <div style={radioDescriptionStyle}>
                  {t(
                    `opportunity:users.candidateSelectionModeDescription.${LIKE_SCREENING_RESTRICTED_PERMISSION}`
                  )}
                </div>
              </div>
            </Radio.Group>
          </div>
        )}
      </div>

      <div style={permissionSectionStyle}>
        <Checkbox
          checked={permissions.includes("write")}
          onChange={handlePermissionToggle("write")}
          style={styleCheckBox}
        >
          {t("opportunity:users.permissionsList.write")}
        </Checkbox>
      </div>
    </div>
  );
};

const InviteUserModal = ({
  visible,
  onCancel,
  onSubmit,
  form: { getFieldDecorator },
  user,
  usersToInvite,
  loading,
  t
}) => {
  return (
    <Form onSubmit={onSubmit}>
      <Modal
        visible={visible}
        onOk={onSubmit}
        okText={
          user
            ? t("opportunity:users.editPermissions")
            : t("opportunity:users.inviteUser")
        }
        cancelText={t("global:cancel")}
        onCancel={onCancel}
        confirmLoading={loading}
        destroyOnClose={true}
        title={
          user
            ? `${t("opportunity:users.editUserPermissions")} ${user.name}`
            : t("opportunity:users.inviteUserToOpportunity")
        }
      >
        {!user && (
          <Form.Item label={t("opportunity:users.userName")}>
            {getFieldDecorator("id", {
              rules: [
                { required: true, message: t("opportunity:users.selectUsers") }
              ],
              initialValue: user ? user.name : undefined
            })(
              <Select
                style={{ width: "100%" }}
                showSearch
                defaultActiveFirstOption={false}
                showArrow={false}
                optionFilterProp="children"
                notFoundContent={t("opportunity:users.noUsersToInvite")}
              >
                {usersToInvite.map(({ id, name }) => (
                  <Option key={id} value={id}>
                    {name}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
        )}
        <Form.Item label={t("opportunity:users.permissions")}>
          {getFieldDecorator("permissions", {
            rules: [
              {
                required: true,
                message: t("opportunity:users.selectPermission")
              }
            ],
            initialValue: getInviteUserFormPermissions(user && user.permissions)
          })(<PermissionsField t={t} />)}
        </Form.Item>
      </Modal>
    </Form>
  );
};

const enhancer = withForm({
  selector: (_, { user }) => user,
  onSubmit: (values, { user, handleSubmit, usersToInvite }) => {
    const guest = {
      ...values,
      permissions: normalizeOpportunityUserPermissions(values.permissions),
      id: user ? user.id : values.id,
      name: user
        ? user.name
        : R.pipe(
            R.find(R.propEq("id", values.id)),
            R.propOr("", "name")
          )(usersToInvite)
    };
    handleSubmit(guest);
  }
});

export default enhancer(InviteUserModal);
